<template>
  <section
    ref="privacyContent"
    class="privacy-policy-module-component"
    :class="{
      'has-pdf-download': hasPdfDownload,
      'use-pdf-download-layout': usePdfDownloadLayout,
    }">
    <div class="module-container bg-white">
      <div class="inner-container">
        <p
          v-if="data.show_date"
          class="text-center mb-24">
          Effective Date: {{ modified }}
        </p>
        <div
          class="top-content"
          v-html="data.content" />
      </div>
    </div>

    <div
      v-if="hasPdfDownload"
      class="privacy-pdf-download"
      :class="{
        'invert-background': invertPdfDownloadBackground,
        'remove-padding-bottom': removePdfDownloadPaddingBottom,
      }">
      <PDFDownloadComponent :data="data.pdf_module_information" />
    </div>

    <div
      v-if="data.text_below_component"
      class="module-container bg-white"
      :class="{ 'pt-7': hasPdfDownload }">
      <div class="inner-container">
        <div
          class="top-content"
          :class="{
            bottom: hasPdfDownload && !hasTable,
            'bottom with-table': hasPdfDownload && hasTable,
          }"
          v-html="data.text_below_component" />
      </div>
    </div>

    <div
      v-if="hasTable"
      class="table-container bg-white">
      <div class="inner-container">
        <p
          v-if="tableTitle"
          class="table-title"
          v-html="tableTitle"></p>

        <div
          v-if="tableColumns.length"
          class="table-scroll"
          data-lenis-prevent>
          <table>
            <thead>
              <tr>
                <th
                  v-for="column in tableColumns"
                  :key="`${column.key}-header`"
                  scope="col"
                  v-html="column.header"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, rowIndex) in tableRows"
                :key="`table-row-${rowIndex}`">
                <td
                  v-for="(cell, columnIndex) in row"
                  :key="`${tableColumns[columnIndex].key}-${rowIndex}`">
                  <span
                    v-if="cell"
                    v-html="cell"></span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <template v-if="useFooterSpacer && data.footer">
      <SpacerComponent :data="negativeFooterSpacerData" />
      <SpacerComponent
        class="privacy-footer-positive-spacer"
        :data="positiveFooterSpacerData" />
    </template>

    <div
      v-if="data.footer"
      class="privacy-policy-footer module-container bg-functional">
      <div class="inner-container">
        <div
          class="footer-content"
          v-html="data.footer" />
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
   {
    "NAME": "PrivacyPolicyComponent",
    "CLASSNAME": "privacy-policy-module-component",
    "URL": "http://vuevitewp.local/test"
   }
  #ENDMETA */

/*
 *   Insert your logic here
 */
import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue'
import { useStore } from '../../stores/main'
import { useModal } from '@/components/composables/useModal'
import PDFDownloadComponent from '@/components/modules/PDFDownloadComponent.vue'
import SpacerComponent from '@/components/modules/SpacerComponent.vue'

const props = defineProps(['data'])
const store = useStore()
const modified = ref()
const privacyContent = ref()
const { openModal } = useModal()
const mailToLinkListeners = []
const cookiePreferencesLinkListeners = []
const hasPdfDownload = computed(
  () =>
    ![false, 0, '0', null, undefined].includes(
      props.data?.enable_pdf_download_module
    ) && Boolean(props.data?.pdf_module_information)
)
const usePdfDownloadLayout = computed(
  () => hasPdfDownload.value && !props.data?.text_below_component
)
const invertPdfDownloadBackground = computed(() =>
  [true, 1, '1'].includes(
    props.data?.pdf_module_information?.invert_background ??
      props.data?.pdf_module_information?.invertBackground
  )
)
const removePdfDownloadPaddingBottom = computed(() =>
  [true, 1, '1'].includes(
    props.data?.pdf_module_information?.remove_padding_bottom ??
      props.data?.pdf_module_information?.removePaddingBottom
  )
)
const hasTable = computed(
  () =>
    [true, 1, '1'].includes(props.data?.add_table) && Boolean(props.data?.table)
)
const useFooterSpacer = computed(
  () => hasTable.value || !usePdfDownloadLayout.value
)
const tableTitle = computed(
  () => props.data?.table?.top_title ?? props.data?.table?.topTitle ?? ''
)
const tableColumns = computed(() => {
  const columns = props.data?.table?.columns

  if (!Array.isArray(columns)) return []

  return columns.map((column, index) => {
    const items = column?.column_content ?? column?.columnContent ?? []
    const cells = Array.isArray(items) ? items : []

    return {
      key: `table-column-${index}`,
      header: cells[0]?.content || '',
      cells: cells.slice(1).map((item) => item?.content || ''),
    }
  })
})
const tableRows = computed(() => {
  const rowCount = Math.max(
    0,
    ...tableColumns.value.map((column) => column.cells.length)
  )

  return Array.from({ length: rowCount }, (_, rowIndex) =>
    tableColumns.value.map((column) => column.cells[rowIndex] || '')
  )
})

const negativeFooterSpacerData = {
  separator: false,
  rounded: 'none',
  spacer_height: -80,
  spacer_height_tablet: -80,
  spacer_height_mobile: -80,
  back_background_color: 'rgba(255, 255, 255, 0)',
  background_color: 'rgba(255, 255, 255, 0)',
}

const positiveFooterSpacerData = {
  separator: false,
  rounded: 'tr',
  spacer_height: 80,
  spacer_height_tablet: 80,
  spacer_height_mobile: 80,
  back_background_color: 'rgba(255, 255, 255, 0)',
  background_color: '#f2f2f2',
}

const handleClick = (url, speedBump = null) => {
  openModal({
    externally: true,
    url,
    speedBump,
  })
}

const bindMailToLinks = async () => {
  await nextTick()

  mailToLinkListeners.forEach(({ element, listener }) => {
    element.removeEventListener('click', listener)
  })
  mailToLinkListeners.length = 0

  const content = privacyContent.value

  if (!content) return

  const allLinks = Array.from(content.querySelectorAll('a'))
  const boundLinks = new Set()
  const addSpeedBumps =
    props.data?.add_speed_bumps ?? props.data?.addSpeedBumps ?? false
  const speedBumpInformation =
    props.data?.speed_bumps_information ??
    props.data?.speedBumpsInformation ??
    []

  if (
    [true, 1, '1'].includes(addSpeedBumps) &&
    Array.isArray(speedBumpInformation)
  ) {
    speedBumpInformation.forEach((item) => {
      const linkId = String(item?.link_id ?? item?.linkId ?? '')
        .trim()
        .replace(/^#/, '')
      const speedBump = item?.speed_bump ?? item?.speedBump

      if (!linkId || !speedBump) return

      const link = allLinks.find((candidate) => candidate.id === linkId)

      if (!link) return

      const listener = (event) => {
        event.preventDefault()
        const url = link.getAttribute('href') || ''

        if (!url) return

        handleClick(url, speedBump)
      }

      link.addEventListener('click', listener)
      mailToLinkListeners.push({ element: link, listener })
      boundLinks.add(link)
    })
  }

  allLinks
    .filter((link) => link.classList.contains('mail-to'))
    .filter((link) => !boundLinks.has(link))
    .forEach((link) => {
      const listener = (event) => {
        event.preventDefault()
        const email = link.getAttribute('href') || ''

        if (!email) {
          return
        }

        handleClick(email)
      }

      link.addEventListener('click', listener)
      mailToLinkListeners.push({ element: link, listener })
    })
}

const openCookiePreferences = () => {
  if (typeof window === 'undefined') return

  if (window.Optanon?.ToggleInfoDisplay) {
    window.Optanon.ToggleInfoDisplay()
  }
}

const bindCookiePreferencesLinks = async () => {
  await nextTick()

  const links =
    privacyContent.value?.querySelectorAll('a.cookie-preferences-trigger') || []

  if (!links.length) {
    return
  }

  cookiePreferencesLinkListeners.forEach(({ element, listener }) => {
    element.removeEventListener('click', listener)
  })
  cookiePreferencesLinkListeners.length = 0

  links.forEach((link) => {
    const listener = (event) => {
      event.preventDefault()
      openCookiePreferences()
    }

    link.addEventListener('click', listener)
    cookiePreferencesLinkListeners.push({ element: link, listener })
  })
}

const getPageData = async () => {
  if (!props.data) {
    return
  }

  await store.getSingleBySlug({
    slug: 'privacy-policy',
    type: 'pages',
  })
  const data = store.getCurrentSingleBySlug({
    slug: 'privacy-policy',
    type: 'pages',
  })
  if (props.data.date_modified) {
    modified.value = getFormattedDate(new Date(props.data.date_modified))
  } else {
    modified.value = getFormattedDate(new Date(data.modified))
  }
}

function getFormattedDate(date) {
  var year = date.getFullYear()
  var months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]
  var month = date.getMonth()
  month = months[month]

  return month + ' ' + year
}

onBeforeMount(() => {
  if (!props.data) {
    return
  }

  getPageData()
})

onMounted(() => {
  bindMailToLinks()
  bindCookiePreferencesLinks()
})

onBeforeUnmount(() => {
  mailToLinkListeners.forEach(({ element, listener }) => {
    element.removeEventListener('click', listener)
  })

  cookiePreferencesLinkListeners.forEach(({ element, listener }) => {
    element.removeEventListener('click', listener)
  })
})
</script>

<style lang="scss" scoped>
.privacy-policy-module-component {
  .privacy-footer-positive-spacer {
    @apply z-2;
  }

  &.has-pdf-download {
    .top-content {
      @apply pb-10;

      &.bottom {
        @apply pb-32;

        &.with-table {
          @apply pb-10;
        }
      }
    }
    .privacy-pdf-download {
      @apply relative z-1 bg-white;

      &.invert-background {
        @apply bg-functional;
      }

      &.remove-padding-bottom {
        @apply pb-0;
      }

      .pdf-download-module-component {
        @apply pt-0;
      }
    }
  }

  &.use-pdf-download-layout {
    .privacy-pdf-download {
      @apply pt-0 pb-16;
    }

    .top-content {
      @apply pb-4;
    }
  }

  .table-container {
    @apply relative z-1 pb-40;

    .table-title {
      @apply text-balanced text-[40px] leading-tight mb-8 font-normal;
    }

    .table-scroll {
      @apply pointer-events-none w-full overflow-x-auto rounded-[20px] border border-solid border-humble/20;

      table {
        @apply w-full table-fixed border-collapse;

        th,
        td {
          @apply px-3 py-3 text-left align-top text-[14px] leading-[18px];
          @apply md:px-5 md:py-4 md:text-[18px] md:leading-[24px];
          overflow-wrap: anywhere;

          + th,
          + td {
            @apply border-l border-solid border-humble/20;
          }
        }

        th {
          @apply border-b border-solid border-humble/20 bg-white text-center font-bold text-humble;
        }

        td {
          @apply bg-white text-humble;

          :deep(*) {
            @apply m-0 text-[14px] leading-[18px] text-humble;
            @apply md:text-[18px] md:leading-[24px];
            overflow-wrap: anywhere;
          }
        }

        tbody tr + tr td {
          @apply border-t border-solid border-humble/20;
        }
      }
    }
  }

  .top-content:deep() {
    ul {
      list-style-type: none !important;
      margin-left: 28px !important;
      li {
        list-style-type: none !important;
        position: relative;
      }
      li::before {
        content: '';
        background: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOCIgaGVpZ2h0PSI4IiB2aWV3Qm94PSIwIDAgOCA4IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cGF0aCBkPSJNMy4wNjAwNiAwLjc4MDk5QzMuMzY2NTIgMC42MDY3NTggMy43NDIwOCAwLjYwNjc1OCA0LjA0ODU0IDAuNzgwOTlMNi4xMjY2NSAxLjk2MjQ2QzYuNDM5MjkgMi4xNDAyMSA2LjYzMjQxIDIuNDcyMTUgNi42MzI0MSAyLjgzMTc5VjUuMTY4MjFDNi42MzI0MSA1LjUyNzg1IDYuNDM5MjkgNS44NTk3OSA2LjEyNjY1IDYuMDM3NTRMNC4wNDg1NCA3LjIxOTAxQzMuNzQyMDggNy4zOTMyNCAzLjM2NjUyIDcuMzkzMjQgMy4wNjAwNiA3LjIxOTAxTDAuOTgxOTQ3IDYuMDM3NTRDMC42NjkzMDMgNS44NTk3OSAwLjQ3NjE4NiA1LjUyNzg1IDAuNDc2MTg2IDUuMTY4MjFWMi44MzE3OUMwLjQ3NjE4NiAyLjQ3MjE1IDAuNjY5MzAzIDIuMTQwMjEgMC45ODE5NDcgMS45NjI0NkwzLjA2MDA2IDAuNzgwOTlaIiBmaWxsPSIjMTNEMzc3Ii8+Cjwvc3ZnPgo=');
        margin-right: 0.5rem;
        width: 10px;
        height: 10px;
        background-repeat: no-repeat;
        background-size: contain;
        position: absolute;
        left: -20px;
        top: 8px;
      }
    }

    ol {
      li {
        counter-increment: double-digit-counter;
        margin-bottom: 10px;
        font-size: 18px;
      }
      li::before {
        content: counter(double-digit-counter, decimal-leading-zero) '.';
        margin-right: 0.5rem;
        top: 0px;
        position: relative;
        background: transparent;
        color: #0e4343;
        width: 28px;
        height: 28px;
        font-size: 18px;
        border-radius: 100px;
        display: inline-flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
      }
    }
    @apply pb-20;
    h2 {
      @apply text-balanced text-[40px] leading-tight mb-8 font-normal;
    }
    .big-text {
      @apply text-[22px] font-medium mb-20;
    }
    h3 {
      @apply text-[26px] text-humble mb-8;
    }

    p {
      @apply text-[18px] text-humble mb-8;
    }

    a {
      @apply underline;
    }

    ul {
      @apply my-10 ml-4 lg:ml-10;
      li {
        @apply text-[18px] text-humble mb-2 list-disc;
      }
    }
  }

  .footer-content:deep() {
    @apply pt-8 pb-16;

    a {
      @apply underline;
    }

    ul {
      list-style-type: none !important;
      margin-left: 28px !important;
      li {
        list-style-type: none !important;
        position: relative;
      }
      li::before {
        content: '';
        background: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOCIgaGVpZ2h0PSI4IiB2aWV3Qm94PSIwIDAgOCA4IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cGF0aCBkPSJNMy4wNjAwNiAwLjc4MDk5QzMuMzY2NTIgMC42MDY3NTggMy43NDIwOCAwLjYwNjc1OCA0LjA0ODU0IDAuNzgwOTlMNi4xMjY2NSAxLjk2MjQ2QzYuNDM5MjkgMi4xNDAyMSA2LjYzMjQxIDIuNDcyMTUgNi42MzI0MSAyLjgzMTc5VjUuMTY4MjFDNi42MzI0MSA1LjUyNzg1IDYuNDM5MjkgNS44NTk3OSA2LjEyNjY1IDYuMDM3NTRMNC4wNDg1NCA3LjIxOTAxQzMuNzQyMDggNy4zOTMyNCAzLjM2NjUyIDcuMzkzMjQgMy4wNjAwNiA3LjIxOTAxTDAuOTgxOTQ3IDYuMDM3NTRDMC42NjkzMDMgNS44NTk3OSAwLjQ3NjE4NiA1LjUyNzg1IDAuNDc2MTg2IDUuMTY4MjFWMi44MzE3OUMwLjQ3NjE4NiAyLjQ3MjE1IDAuNjY5MzAzIDIuMTQwMjEgMC45ODE5NDcgMS45NjI0NkwzLjA2MDA2IDAuNzgwOTlaIiBmaWxsPSIjMTNEMzc3Ii8+Cjwvc3ZnPgo=');
        margin-right: 0.5rem;
        width: 10px;
        height: 10px;
        background-repeat: no-repeat;
        background-size: contain;
        position: absolute;
        left: -20px;
        top: 8px;
      }
    }

    ol {
      li {
        counter-increment: double-digit-counter;
        margin-bottom: 10px;
        font-size: 18px;
      }
      li::before {
        content: counter(double-digit-counter, decimal-leading-zero) '.';
        margin-right: 0.5rem;
        top: 0px;
        position: relative;
        background: transparent;
        color: #0e4343;
        width: 28px;
        height: 28px;
        font-size: 18px;
        border-radius: 100px;
        display: inline-flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
      }
    }

    h2 {
      @apply text-balanced text-[40px] mb-8 font-normal;
    }
    .big-text {
      @apply text-[22px] font-medium mb-20;
    }
    h3 {
      @apply text-[26px] text-humble mb-8;
    }

    p {
      @apply text-[18px] text-humble mb-8;
    }

    ul {
      @apply my-10 ml-10;
      li {
        @apply text-[18px] text-humble mb-2 list-disc;
      }
    }
  }
}
</style>
